import { useState, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { Download, Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard, downloadCanvas } from '@/lib/utils';
import { toast } from 'sonner';

const FORMATS = [
  { value: 'CODE128', label: 'Code 128 — alphanumeric' },
  { value: 'CODE39', label: 'Code 39 — short alphanumeric' },
  { value: 'EAN13', label: 'EAN-13 — retail (13 digits)' },
  { value: 'EAN8', label: 'EAN-8 — small retail (8 digits)' },
  { value: 'UPC', label: 'UPC-A — US retail (12 digits)' },
  { value: 'ITF14', label: 'ITF-14 — shipping' },
  { value: 'MSI', label: 'MSI — inventory' },
  { value: 'pharmacode', label: 'Pharmacode — pharma' },
];

const SAMPLES: Record<string, string> = {
  CODE128: 'UTILITY-OS-2026',
  CODE39: 'UTILITY-2026',
  EAN13: '5901234123457',
  EAN8: '12345670',
  UPC: '042100005264',
  ITF14: '12345678901231',
  MSI: '1234567',
  pharmacode: '1234',
};

export function Barcode() {
  const [text, setText] = useState('UTILITY-OS-2026');
  const [format, setFormat] = useState('CODE128');
  const [width, setWidth] = useState(2);
  const [height, setHeight] = useState(80);
  const [showText, setShowText] = useState(true);
  const [bg, setBg] = useState('#ffffff');
  const [fg, setFg] = useState('#000000');
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    try {
      JsBarcode(c, text, {
        format,
        width,
        height,
        displayValue: showText,
        background: bg,
        lineColor: fg,
        fontSize: 14,
        margin: 12,
      });
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid input for this format');
    }
  }, [text, format, width, height, showText, bg, fg]);

  return (
    <div>
      <ToolHeader
        toolId="barcode-generator"
        title="Barcode generator"
        description="Generate Code 128, EAN, UPC, ITF, MSI, and pharma barcodes. Configure size, color, and label."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <Field label="Format">
              <Select
                value={format}
                onChange={(e) => {
                  setFormat(e.target.value);
                  setText(SAMPLES[e.target.value] ?? text);
                }}
                options={FORMATS}
              />
            </Field>
            <Field label="Value" className="mt-3">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="font-mono"
                invalid={!!error}
              />
            </Field>
            {error && <div className="mt-2 text-xs text-danger">{error}</div>}
          </Card>

          <Card className="!p-3 overflow-hidden">
            <div className="bg-bg-inset rounded p-6 flex items-center justify-center min-h-[200px]">
              <canvas ref={canvasRef} className="max-w-full" />
            </div>
          </Card>
        </div>

        <Card className="space-y-3">
          <Field label="Bar width" hint={`${width}px`}>
            <Slider value={width} onChange={setWidth} min={1} max={6} showValue={false} />
          </Field>
          <Field label="Height" hint={`${height}px`}>
            <Slider value={height} onChange={setHeight} min={30} max={200} step={10} showValue={false} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Foreground">
              <input
                type="color"
                value={fg}
                onChange={(e) => setFg(e.target.value)}
                className="!w-full !h-9"
              />
            </Field>
            <Field label="Background">
              <input
                type="color"
                value={bg}
                onChange={(e) => setBg(e.target.value)}
                className="!w-full !h-9"
              />
            </Field>
          </div>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="text-fg-muted">Show value</span>
            <Switch checked={showText} onChange={setShowText} />
          </label>
          <div className="grid grid-cols-2 gap-2 border-t border-border pt-3">
            <Button
              size="sm"
              variant="secondary"
              disabled={!!error}
              onClick={async () => {
                if (!canvasRef.current) return;
                const url = canvasRef.current.toDataURL();
                const ok = await copyToClipboard(url);
                if (ok) toast.success('Data URL copied');
              }}
            >
              <Copy size={11} />
              Data URL
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!!error}
              onClick={() => {
                if (canvasRef.current) downloadCanvas(canvasRef.current, 'barcode.png');
              }}
            >
              <Download size={11} />
              PNG
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Barcode;
